import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChuteStatus } from '../model/sortingSettings';
import { type Assignments, type ChuteSettingsSnapshot, useChuteSettingsStore } from '../model/useChuteSettingsStore';
import { getBagCodes, getChutes } from '.';

function buildSnapshot(
  chutesRes: Awaited<ReturnType<typeof getChutes>>,
  bagCodesRes: Awaited<ReturnType<typeof getBagCodes>>,
): ChuteSettingsSnapshot {
  const directionMap = new Map<number, { id: number; name: string }>();

  for (const item of bagCodesRes.items ?? []) {
    const id = item.sortingDirectoryId;
    directionMap.set(id, { id, name: item.bagCode });
  }

  const chutes = (chutesRes.items ?? []).map((c) => ({
    id: c.id,
    name: `Chute ${c.number}`,
    isDefault: c.status !== ChuteStatus.Locked,
    status: c.status,
  }));

  const assignments: Assignments = {};
  for (const c of chutesRes.items ?? []) {
    const chuteId = c.id;

    const ids = (c.sortingDirectories ?? []).map((sd) => {
      const id = sd.id;
      if (!directionMap.has(id)) {
        directionMap.set(id, { id, name: sd.bagCode });
      }
      return id;
    });

    assignments[chuteId] = ids;
  }

  for (const c of chutes) {
    if (!assignments[c.id]) assignments[c.id] = [];
  }

  const directions = Array.from(directionMap.values());

  return {
    directions,
    chutes: chutes,
    assignments,
  };
}

export function useInitChuteSettings() {
  const load = useChuteSettingsStore((s) => s.load);

  const query = useQuery({
    queryKey: ['chute-settings', 'init'],
    queryFn: async () => {
      const [chutesRes, bagCodesRes] = await Promise.all([getChutes(), getBagCodes()]);
      return buildSnapshot(chutesRes, bagCodesRes);
    },
    retry: 1,
    staleTime: 30_000,
  });

  useEffect(() => {
    useChuteSettingsStore.setState({
      isLoading: query.isPending,
      error: query.isError ? (query.error instanceof Error ? query.error.message : 'Failed to load.') : null,
    });
  }, [query.isPending, query.isError, query.error]);

  useEffect(() => {
    if (query.data) load(query.data);
    
  }, [query.data, load]);

  return query;
}
